import { createContext, useContext, useCallback, useRef } from "react";
import { fetchTopicById } from "../../../apis/CourseApis";

/**
 * TopicCacheContext
 * 
 * Provides a caching layer for topic data to enable instant navigation.
 * - Caches fetched topics in memory
 * - Prefetches adjacent topics for smooth Next/Previous navigation
 * - Implements TTL-based cache invalidation (5 minutes)
 */

const TopicCacheContext = createContext(null);

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function TopicCacheProvider({ children }) {
  // Cache structure: { [topicId]: { data: topic, timestamp: number } }
  const cacheRef = useRef({});
  const pendingRef = useRef({}); // Track in-flight requests to avoid duplicates

  /**
   * Get a topic from cache if valid, otherwise return null
   */
  const getFromCache = useCallback((topicId) => {
    const cached = cacheRef.current[topicId];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > CACHE_TTL_MS) {
      // Cache expired, remove it
      delete cacheRef.current[topicId];
      return null;
    }

    return cached.data;
  }, []);

  /**
   * Add topic to cache
   */
  const addToCache = useCallback((topicId, data) => {
    cacheRef.current[topicId] = {
      data,
      timestamp: Date.now(),
    };
  }, []);

  /**
   * Fetch topic with caching - returns cached data if available
   */
  const fetchTopic = useCallback(async (topicId) => {
    // Check cache first
    const cached = getFromCache(topicId);
    if (cached) return cached;

    // Check if request is already in flight
    if (pendingRef.current[topicId]) {
      return pendingRef.current[topicId];
    }

    // Fetch and cache
    try {
      const promise = fetchTopicById(topicId);
      pendingRef.current[topicId] = promise;

      const data = await promise;
      addToCache(topicId, data);
      return data;
    } finally {
      delete pendingRef.current[topicId];
    }
  }, [getFromCache, addToCache]);

  /**
   * Prefetch topics in background (non-blocking)
   * Call this to preload adjacent topics for instant navigation
   */
  const prefetchTopics = useCallback((topicIds) => {
    for (const topicId of topicIds) {
      if (!topicId) continue;
      
      // Skip if already cached or in flight
      if (getFromCache(topicId) || pendingRef.current[topicId]) {
        continue;
      }

      // Fire and forget - don't await, just start the fetch
      fetchTopic(topicId).catch(() => {
        // Silently ignore prefetch errors
      });
    }
  }, [fetchTopic, getFromCache]);

  /**
   * Clear a specific topic from cache (useful after edits)
   */
  const invalidate = useCallback((topicId) => {
    delete cacheRef.current[topicId];
  }, []);

  /**
   * Clear entire cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current = {};
  }, []);

  const value = {
    fetchTopic,
    prefetchTopics,
    getFromCache,
    invalidate,
    clearCache,
  };

  return (
    <TopicCacheContext.Provider value={value}>
      {children}
    </TopicCacheContext.Provider>
  );
}

export function useTopicCache() {
  const context = useContext(TopicCacheContext);
  // Return null if not within provider (graceful fallback)
  // This allows TopicView to work even if provider is missing
  return context;
}

export default TopicCacheContext;
